import { entries, fromEntries } from '@direct-frontend/stdlib';
import { getSettingValue } from '@adv-frontend/yastore-modules/settings-tree';
import { useGetSettings } from '../../api';
import { SETTINGS_ROOT_SLUG } from '../../constants';
import { type GetValueByPathResponses, type SettingsLeafPaths } from '../../types';
import { getSettingPatchFunction } from '../getSettingPatchFunction';

type ValuesType<Name extends string> = Record<Name, { path: SettingsLeafPaths }>;

export type SettingsValue<LeafPath extends SettingsLeafPaths> = {
    value: GetValueByPathResponses[LeafPath] | undefined;
    patch: ReturnType<typeof getSettingPatchFunction<LeafPath>>;
    path: LeafPath;
};

export interface SettingsValueInterface<T> {
    value: T | undefined;
    patch: (value: T) => Promise<boolean>;
}

type SettingsValues<Values extends ValuesType<string>> = {
    [Key in keyof Values]: SettingsValue<Values[Key]['path']>;
};

export function useSettingsFactory<Values extends ValuesType<string>>(values: Values) {
    const { data, status, refetch } = useGetSettings({});

    const transformedValues: SettingsValues<Values> = fromEntries(
        entries(values).map(([key, value]) => [
            key,
            {
                value: getSettingValue<SettingsLeafPaths, GetValueByPathResponses>(
                    data,
                    SETTINGS_ROOT_SLUG,
                    value.path
                ),
                patch: getSettingPatchFunction(value.path),
                path: value.path,
            },
        ])
    );

    return {
        values: transformedValues,
        status,
        refetch,
    };
}
